import { type FC, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { useNotificationSummaryViewUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { getPlaybookEventName, type IEventState, loadEvent } from "@/redux/modules/event.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";
import EventWorkflowView from "@/ui-modules/entities/EventWorkflowView.tsx";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import type { IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import EventSummaryView from "./EventSummaryView.js";

const EventDetailsPage: FC = () => {
  // redux
  const dispatch = useDispatch<ReduxDispatch>();
  const { event, isLoading } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);
  // router
  const { eventId } = useParams<"eventId">();
  useNotificationSummaryViewUpdate(() => dispatch(loadEvent(Number(eventId))), Number(eventId));

  useEffect(() => {
    void dispatch(loadEvent(Number(eventId)));
  }, [dispatch, eventId]);

  const getNavItems = useCallback((): IRouteNavItemProps[] => {
    const navItems: IRouteNavItemProps[] = [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <EventSummaryView />,
        default: true,
      },
    ];

    if (event?.workflowP2ExtendedConfig != null) {
      navItems.push({
        label: "Preview",
        route: "preview",
        pageComponent: <EventWorkflowView />,
        default: true,
        disabled: !event?.workflowP2ExtendedConfig,
        disabledTooltip: "Preview is only available for Processor Playbooks",
      });
    }
    return navItems;
  }, [event]);

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.EVENT}
      entityId={Number(eventId)}
      getDisplayName={() => getPlaybookEventName(event)}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export { EventDetailsPage };
